import { useEffect, useState } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';

import { fetchMetrics } from '../../../services/syntiro_honey/data_ingestion.service';

/** Matches `MetricAutocomplete` — long enough that typing does not fire a request per key. */
const SEARCH_DEBOUNCE_MS = 300;

/** How many catalogue matches to offer per search. */
const SUGGESTION_LIMIT = 20;

interface MetricHintsFieldProps {
  /**
   * Current hints (controlled). An array end to end — the wire shape is
   * `metricHints: string[]`, so no delimiter round trip exists and a catalogue
   * name containing a comma survives as ONE hint.
   */
  value: Array<string>;
  /** Called with the next hints. */
  onChange: (value: Array<string>) => void;
  /** Placeholder shown while no hint is chosen. */
  placeholder: string;
  /** Helper text under the field. */
  helperText: string;
  /** Disable the control. */
  disabled?: boolean;
}

/**
 * Metric-coverage hints as removable chips, suggested from the live metric
 * catalogue.
 *
 * Free text alone asked the uploader to guess Syntiro's vocabulary — a hint only
 * helps the extraction prompt when it uses words the catalogue also uses. This
 * offers the real names (`GET /metrics`, the same source as the review screen's
 * `MetricAutocomplete`) while staying `freeSolo`, because a hint is guidance for a
 * prompt and not a foreign key: a metric the catalogue does not carry yet is still
 * a useful thing to tell the model.
 *
 * A failed lookup leaves the previous suggestions and never blocks typing — hints
 * are optional, so the catalogue being unreachable must not stop an upload.
 * @param {Array<string>} value Current hints.
 * @param {(value: Array<string>) => void} onChange Receives the next hints.
 * @param {string} placeholder Placeholder shown while no hint is chosen.
 * @param {string} helperText Helper text under the field.
 * @param {boolean | undefined} disabled Disable the control.
 * @returns {JSX.Element}
 */
export default function MetricHintsField({
  value,
  onChange,
  placeholder,
  helperText,
  disabled
}: MetricHintsFieldProps) {
  const [options, setOptions] = useState<Array<string>>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const hints = value;

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      fetchMetrics(inputValue, SUGGESTION_LIMIT)
        .then((items) => {
          if (!cancelled) {
            setOptions(items.map((item) => item.name));
          }
        })
        .catch(() => undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [inputValue]);

  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={options}
      value={hints}
      disabled={disabled}
      onChange={(_event, next: Array<string>) =>
        onChange(next.map((hint: string) => hint.trim()).filter((hint: string) => hint !== ''))
      }
      inputValue={inputValue}
      onInputChange={(_event, next: string) => setInputValue(next)}
      // The server already searched; filtering the page again locally would hide
      // matches whose relevance the API decided.
      filterOptions={(fetched) => fetched}
      renderTags={(values: Array<string>, getTagProps) =>
        values.map((hint: string, index: number) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} size="small" label={hint} {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Metrics"
          // Only while empty: MUI renders the placeholder alongside existing chips,
          // where a list of examples reads as another chip.
          placeholder={hints.length === 0 ? placeholder : undefined}
          helperText={helperText}
          slotProps={{ inputLabel: { ...params.InputLabelProps, shrink: true } }}
        />
      )}
    />
  );
}
